<template>
  <div class="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900/70 via-black/50 to-gray-900/70">
    <div class="max-w-7xl mx-auto">
      <div class="mb-8 bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 p-6 relative">
        <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
        <div class="flex justify-between items-center relative">
          <h1 class="text-3xl font-bold text-amber-300 flex items-center">
            <i class="fas fa-trophy mr-3 text-amber-400"></i> Classement ELO
          </h1>
        </div>
      </div>

      <!-- Message d'erreur -->
      <div v-if="error" class="mb-4 p-4 bg-red-900/30 border border-red-700/30 rounded-lg text-red-300">
        <div class="flex justify-between items-center">
          <span class="flex items-center"><i class="fas fa-exclamation-triangle mr-2"></i> Impossible de charger le classement.</span>
          <button 
            @click="retryLoading"
            class="px-4 py-2 bg-red-700/30 hover:bg-red-700/50 rounded-lg transition-colors duration-200 text-red-200 flex items-center"
          >
            <i class="fas fa-sync-alt mr-2"></i> Réessayer
          </button>
        </div>
      </div>

      <!-- Indicateur de chargement -->
      <div v-if="isLoading" class="mb-4 p-4 bg-amber-900/30 border border-amber-700/30 rounded-lg text-amber-300 flex items-center">
        <i class="fas fa-spinner fa-spin mr-2"></i> Chargement du classement...
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 p-6 relative box-glow hover:border-amber-600/70 transition-all duration-300">
          <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
          <div class="relative">
            <h3 class="text-xl font-bold text-amber-300 mb-2 flex items-center">
              <i class="fas fa-crown mr-2 text-yellow-400"></i> Meilleur joueur
            </h3>
            <p class="text-2xl font-bold text-amber-400 flex items-center">
              <i class="fas fa-user-circle mr-2"></i> {{ topPlayer?.username || "Aucun joueur" }}
            </p>
            <div class="mt-2 inline-block px-3 py-1 bg-amber-900/30 rounded-lg border border-amber-700/30 shadow-inner">
              <p class="text-amber-200 flex items-center">
                <i class="fas fa-star mr-1 text-yellow-400"></i> Score ELO: <span class="font-semibold ml-1">{{ topPlayer?.elo || "N/A" }}</span>
              </p>
            </div>
          </div>
        </div>
        
        <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 p-6 relative box-glow hover:border-amber-600/70 transition-all duration-300">
          <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
          <div class="relative">
            <h3 class="text-xl font-bold text-amber-300 mb-2 flex items-center">
              <i class="fas fa-gamepad mr-2 text-amber-400"></i> Matchs joués
            </h3>
            <p class="text-2xl font-bold text-amber-400 animate-pulse-slow">{{ totalMatches }}</p>
            <div class="mt-2 inline-block px-3 py-1 bg-amber-900/30 rounded-lg border border-amber-700/30 shadow-inner">
              <p class="text-amber-200 flex items-center">
                <i class="fas fa-chart-line mr-1"></i> au total
              </p>
            </div>
          </div>
        </div>
        
        <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 p-6 relative box-glow hover:border-amber-600/70 transition-all duration-300">
          <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
          <div class="relative">
            <h3 class="text-xl font-bold text-amber-300 mb-2 flex items-center">
              <i class="fas fa-users mr-2 text-amber-400"></i> Joueurs actifs
            </h3>
            <p class="text-2xl font-bold text-amber-400 animate-pulse-slow">{{ activePlayers }}</p>
            <div class="mt-2 inline-block px-3 py-1 bg-amber-900/30 rounded-lg border border-amber-700/30 shadow-inner">
              <p class="text-amber-200 flex items-center">
                <i class="fas fa-shield-alt mr-1"></i> dans la ligue
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
        <div class="px-5 py-5 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
          <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
          <div class="relative p-2">
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Rechercher un joueur..."
              class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100 pl-12 transition-shadow duration-300 shadow-inner shadow-amber-900/20"
            />
            <i class="fas fa-search text-amber-500/50 absolute left-6 top-6"></i>
          </div>
        </div>

        <div class="overflow-x-auto relative">
          <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
          <table class="min-w-full divide-y divide-amber-800/60">
            <thead class="bg-gradient-to-r from-amber-950/80 via-black/95 to-amber-950/80">
              <tr>
                <th 
                  class="px-6 py-4 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider cursor-pointer transition-colors duration-200 hover:text-amber-200"
                  @click="sortBy('rank')"
                >
                  <div class="flex items-center">
                    <i class="fas fa-hashtag mr-2"></i> Rang
                    <i class="ml-1 fas" :class="sortDirection === 'asc' && sortField === 'rank' ? 'fa-sort-up' : sortDirection === 'desc' && sortField === 'rank' ? 'fa-sort-down' : 'fa-sort text-amber-600/50'"></i>
                  </div>
                </th>
                <th 
                  class="px-6 py-4 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider cursor-pointer transition-colors duration-200 hover:text-amber-200"
                  @click="sortBy('username')"
                >
                  <div class="flex items-center">
                    <i class="fas fa-user mr-2"></i> Nom
                    <i class="ml-1 fas" :class="sortDirection === 'asc' && sortField === 'username' ? 'fa-sort-up' : sortDirection === 'desc' && sortField === 'username' ? 'fa-sort-down' : 'fa-sort text-amber-600/50'"></i>
                  </div>
                </th>
                <th 
                  class="px-6 py-4 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider cursor-pointer transition-colors duration-200 hover:text-amber-200"
                  @click="sortBy('elo')"
                >
                  <div class="flex items-center">
                    <i class="fas fa-star mr-2"></i> Score ELO
                    <i class="ml-1 fas" :class="sortDirection === 'asc' && sortField === 'elo' ? 'fa-sort-up' : sortDirection === 'desc' && sortField === 'elo' ? 'fa-sort-down' : 'fa-sort text-amber-600/50'"></i>
                  </div>
                </th>
                <th 
                  class="px-6 py-4 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider cursor-pointer transition-colors duration-200 hover:text-amber-200"
                  @click="sortBy('gamesPlayed')"
                >
                  <div class="flex items-center">
                    <i class="fas fa-gamepad mr-2"></i> Matchs
                    <i class="ml-1 fas" :class="sortDirection === 'asc' && sortField === 'gamesPlayed' ? 'fa-sort-up' : sortDirection === 'desc' && sortField === 'gamesPlayed' ? 'fa-sort-down' : 'fa-sort text-amber-600/50'"></i>
                  </div>
                </th>
                <th 
                  class="px-6 py-4 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider cursor-pointer transition-colors duration-200 hover:text-amber-200"
                  @click="sortBy('winRate')"
                >
                  <div class="flex items-center">
                    <i class="fas fa-percentage mr-2"></i> Taux de victoire
                    <i class="ml-1 fas" :class="sortDirection === 'asc' && sortField === 'winRate' ? 'fa-sort-up' : sortDirection === 'desc' && sortField === 'winRate' ? 'fa-sort-down' : 'fa-sort text-amber-600/50'"></i>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-amber-900/30 relative">
              <tr v-for="(player, index) in filteredPlayers" :key="player.id" 
                  :class="[
                    isCurrentUser(player.id) ? 'bg-amber-900/30' : index % 2 === 0 ? 'bg-black/80' : 'bg-amber-950/40',
                    'hover:bg-amber-900/40 transition-colors duration-200 transform hover:scale-101'
                  ]">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="inline-flex items-center justify-center h-8 w-8 rounded-full shadow-md border border-amber-600/40 font-bold text-sm" 
                       :class="getRankClass(player.rank)">
                    {{ player.rank }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <i class="fas fa-user-circle mr-2 text-amber-400"></i>
                    <div class="text-sm font-medium text-amber-200">
                      {{ player.username }}
                    </div>
                    <i v-if="isCurrentUser(player.id)" class="fas fa-user-check ml-2 text-amber-300" title="C'est vous"></i>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="px-3 py-1 inline-flex text-sm text-amber-300 bg-amber-900/30 rounded-full border border-amber-700/30 shadow-inner items-center">
                    <i class="fas fa-star mr-1 text-yellow-400"></i> {{ player.elo }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-amber-200 flex items-center">
                  <i class="fas fa-gamepad mr-2 text-amber-500/70"></i> {{ player.gamesPlayed }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="px-3 py-1 inline-flex text-sm text-amber-300 bg-amber-900/30 rounded-full border border-amber-700/30 shadow-inner items-center">
                    <i class="fas fa-chart-pie mr-1 text-amber-500/70"></i> {{ player.winRate }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Légende de classement -->
        <div class="p-4 border-t border-amber-800/60 bg-gradient-to-r from-amber-950/80 via-black/95 to-amber-950/80 relative">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div class="flex items-center">
              <div class="inline-flex items-center justify-center h-6 w-6 rounded-full shadow-md border border-amber-600/40 font-bold text-xs text-black bg-gradient-to-r from-amber-300 to-amber-400 mr-2">1</div>
              <span class="text-xs text-amber-200">Champion</span>
            </div>
            <div class="flex items-center">
              <div class="inline-flex items-center justify-center h-6 w-6 rounded-full shadow-md border border-amber-600/40 font-bold text-xs text-black bg-gradient-to-r from-gray-300 to-gray-400 mr-2">2</div>
              <span class="text-xs text-amber-200">Vice-champion</span>
            </div>
            <div class="flex items-center">
              <div class="inline-flex items-center justify-center h-6 w-6 rounded-full shadow-md border border-amber-600/40 font-bold text-xs text-black bg-gradient-to-r from-amber-700 to-amber-800 mr-2">3</div>
              <span class="text-xs text-amber-200">Médaille de bronze</span>
            </div>
            <div class="flex items-center">
              <div class="inline-flex items-center justify-center h-6 w-6 rounded-full shadow-md border border-amber-600/40 font-bold text-xs text-amber-200 bg-gradient-to-r from-amber-900 to-gray-800 mr-2">4+</div>
              <span class="text-xs text-amber-200">Classement régulier</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

// Remplacer les refs par des computed properties du store
const rankings = computed(() => {
  const data = store.getters['classement/allClassement']
  // Ajouter la propriété rank si elle n'existe pas (ou ajuster selon la réponse de l'API)
  return data.map((player, index) => {
    if (!player.rank) {
      player.rank = index + 1
    }
    return player
  })
})
const isLoading = computed(() => store.getters['classement/isLoading'])
const error = computed(() => store.getters['classement/error'])
const searchQuery = ref('')
const sortField = ref('rank')
const sortDirection = ref('asc')

// Statistiques calculées
const topPlayer = computed(() => {
  const players = store.getters['classement/topPlayers']
  return players && players.length > 0 ? players[0] : null
})
const totalMatches = computed(() => store.getters['classement/globalStats']?.totalMatches || 0)
const activePlayers = computed(() => store.getters['classement/globalStats']?.activePlayers || 0)

// Trier les joueurs
const sortBy = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'asc'
  }
}

// Filtrer les joueurs par recherche
const filteredPlayers = computed(() => {
  if (!searchQuery.value) return rankings.value
  
  const query = searchQuery.value.toLowerCase()
  return rankings.value.filter(player => 
    player.username.toLowerCase().includes(query)
  )
})

// Vérifie si c'est l'utilisateur courant
const isCurrentUser = (userId) => {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  return currentUser.id === userId
}

// Retourne la classe pour le rang
const getRankClass = (rank) => {
  const classes = [
    'text-black bg-gradient-to-r from-amber-300 to-amber-400', // 1er
    'text-black bg-gradient-to-r from-gray-300 to-gray-400',   // 2e
    'text-black bg-gradient-to-r from-amber-700 to-amber-800', // 3e
    'text-amber-200 bg-gradient-to-r from-amber-900 to-gray-800',  // autres
  ];
  return rank <= 3 ? classes[rank - 1] : classes[3];
}

onMounted(async () => {
  try {
    // Charger toutes les données nécessaires
    await Promise.all([
      store.dispatch('classement/fetchClassement'),
      store.dispatch('classement/fetchTopPlayers'),
      store.dispatch('classement/fetchGlobalStats')
    ])
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error)
    store.commit('classement/setError', 'Impossible de charger les données du classement. Veuillez réessayer plus tard.')
  }
})

// Ajout d'une fonction pour réessayer le chargement
const retryLoading = async () => {
  store.commit('classement/setError', null)
  try {
    await Promise.all([
      store.dispatch('classement/fetchClassement'),
      store.dispatch('classement/fetchTopPlayers'),
      store.dispatch('classement/fetchGlobalStats')
    ])
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error)
    store.commit('classement/setError', 'Impossible de charger les données du classement. Veuillez réessayer plus tard.')
  }
}
</script>

<style scoped>
.pattern-warhammer {
  background-image: 
    url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 0L0 12l12 12 12-12z' fill='%23D4AF37' fill-opacity='0.2'/%3E%3C/svg%3E"),
    url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0l20 20-20 20L0 20z' fill='%23D4AF37' fill-opacity='0.1'/%3E%3C/svg%3E");
  background-size: 30px 30px, 60px 60px;
}

.box-glow {
  box-shadow: 0 0 30px rgba(217, 119, 6, 0.15), 
              0 5px 20px rgba(0, 0, 0, 0.3), 
              inset 0 0 15px rgba(217, 119, 6, 0.05);
  transition: box-shadow 0.3s ease-in-out;
}

.box-glow:hover {
  box-shadow: 0 0 35px rgba(217, 119, 6, 0.25), 
              0 5px 20px rgba(0, 0, 0, 0.4), 
              inset 0 0 20px rgba(217, 119, 6, 0.1);
}

.animate-pulse-slow {
  animation: pulse 3s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

.hover\:scale-101:hover {
  transform: scale(1.01);
}
</style> 